import { useEffect, useState, type ReactNode } from 'react'
import { ContextMenu } from '@/components/features/context-menu'
import {
  getExplorerTabTitle,
  getTabExtensionContext,
  type OpenTab,
} from '@/components/features/document-tabs'
import { buildAppUrl, type WorkspaceRouteInfo } from '@/lib/app-routes'
import { isHomeTab } from '@/components/layout/resizable-layout.handlers/shared'
import { findPinnedSidebarShortcut, getSidebarShortcuts } from '@/lib/api/shortcuts'

/** Handlers shared by horizontal tabs and Open Tabs sidebar rows (ADR-0020). */
export type TabMenuHandlers = {
  onTabToggleGroupCollapsed?: (workspaceGroupId: string) => void
  onTabPinToggle?: (tabId: string) => void
  onTabClose?: (tabId: string) => void
  onTabCloseOthers?: (tabId: string) => void
  onTabShowFileLocation?: (tabId: string) => void
  onTabRename?: (tabId: string) => void
  onTabMove?: (tabId: string) => void
  onTabExportPDF?: (tabId: string) => void
  onTabAddToChat?: (tabId: string) => void
  onTabMoveToWindow?: (tabId: string) => void
  onTabCopyToWindow?: (tabId: string) => void
  getTabFilePath?: (tabId: string) => Promise<string | null>
  onTabToggleBookmark?: (tabId: string) => Promise<void>
  getTabBookmarkStatus?: (
    tabId: string,
  ) => Promise<{ isBookmarked: boolean | null; isToggling: boolean }>
}

type TabContextMenuHostProps = {
  tab: OpenTab
  /** Total visible tabs - used to hide Close on sole Home. */
  totalTabs: number
  workspacesRoute?: WorkspaceRouteInfo[]
  /** Set for tabs inside a workspace group so the menu can fold that group. */
  workspaceGroupId?: string
  groupCollapsed?: boolean
  handlers: TabMenuHandlers
  children: (api: {
    onContextMenu: (e: React.MouseEvent) => void
  }) => ReactNode
}

/**
 * Owns tab context-menu state + ContextMenu rendering.
 * SortableTab and Open Tabs rows share this so menu items stay one source (ADR-0020).
 */
export function TabContextMenuHost({
  tab,
  totalTabs,
  workspacesRoute = [],
  workspaceGroupId,
  groupCollapsed = false,
  handlers,
  children,
}: TabContextMenuHostProps) {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [tabBookmarkStatus, setTabBookmarkStatus] = useState<{
    isBookmarked: boolean | null
    isToggling: boolean
  } | null>(null)
  const [tabFilePath, setTabFilePath] = useState<string | null>(null)
  const [sidebarPinned, setSidebarPinned] = useState(false)

  useEffect(() => {
    if (
      contextMenu &&
      handlers.getTabFilePath &&
      tab.type !== 'explorer' &&
      tab.type !== 'chat-conversation' &&
      !tab.id.startsWith('new-')
    ) {
      void handlers.getTabFilePath(tab.id).then((path) => {
        setTabFilePath(path)
      })
    }
  }, [contextMenu, tab.id, tab.type, handlers.getTabFilePath])

  const handleContextMenu = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ x: e.pageX, y: e.pageY })

    if (handlers.getTabBookmarkStatus) {
      try {
        const status = await handlers.getTabBookmarkStatus(tab.id)
        setTabBookmarkStatus(status)
      } catch (err) {
        console.warn('[TabContextMenuHost] Could not load bookmark status:', err)
        setTabBookmarkStatus({ isBookmarked: null, isToggling: false })
      }
    } else {
      setTabBookmarkStatus(null)
    }

  }

  useEffect(() => {
    if (!contextMenu) {
      setSidebarPinned(false)
      return
    }
    const keys = [tab.id, tab.explorerData?.folderPath, tabFilePath].filter(
      (key): key is string => !!key,
    )
    if (keys.length === 0) return
    let cancelled = false
    void getSidebarShortcuts()
      .then((result) => {
        if (cancelled) return
        setSidebarPinned(
          keys.some((key) => findPinnedSidebarShortcut(result.shortcuts || [], key) != null),
        )
      })
      .catch(() => {
        if (!cancelled) setSidebarPinned(false)
      })
    return () => {
      cancelled = true
    }
  }, [contextMenu, tab.id, tab.explorerData?.folderPath, tabFilePath])

  const closeContextMenu = () => {
    setContextMenu(null)
    setTabFilePath(null)
  }

  const explorerFolderPath =
    tab.type === 'explorer' ? (tab.explorerData?.folderPath ?? null) : null
  const isNewTab = tab.id.startsWith('new-')
  const hasFilePath = tabFilePath !== null || tab.id.includes('/')

  return (
    <>
      {children({ onContextMenu: handleContextMenu })}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
          context={{
            type: 'tab',
            value:
              tab.type === 'explorer'
                ? getExplorerTabTitle(tab, workspacesRoute)
                : tab.title,
            tabId: tab.id,
            tabType: tab.type,
            documentId: tab.id,
            filePath:
              tabFilePath ||
              explorerFolderPath ||
              (tab.id.includes('/') ? tab.id : undefined),
            path:
              tabFilePath ||
              explorerFolderPath ||
              (tab.id.includes('/') ? tab.id : undefined),
            isPinned: tab.isPinned,
            sidebarPinned,
            hasFilePath,
            isNewTab,
            hideCloseTab: isHomeTab(tab) && totalTabs <= 1,
            shareUrl: buildAppUrl({ tab, workspaces: workspacesRoute }),
            workspaceGroupId,
            groupCollapsed,
            ...getTabExtensionContext(tab),
          }}
          onTabToggleGroupCollapsed={handlers.onTabToggleGroupCollapsed}
          onTabShowFileLocation={handlers.onTabShowFileLocation}
          onTabRename={handlers.onTabRename}
          onTabMove={handlers.onTabMove}
          onTabExportPDF={handlers.onTabExportPDF}
          onTabAddToChat={handlers.onTabAddToChat}
          onTabPinToggle={handlers.onTabPinToggle}
          onTabClose={handlers.onTabClose}
          onTabCloseOthers={handlers.onTabCloseOthers}
          onTabMoveToWindow={handlers.onTabMoveToWindow}
          onTabCopyToWindow={handlers.onTabCopyToWindow}
          onTabToggleBookmark={
            handlers.onTabToggleBookmark
              ? async (tabId) => {
                  await handlers.onTabToggleBookmark?.(tabId)
                  if (handlers.getTabBookmarkStatus) {
                    try {
                      const newStatus = await handlers.getTabBookmarkStatus(tab.id)
                      setTabBookmarkStatus(newStatus)
                    } catch (err) {
                      console.warn(
                        '[TabContextMenuHost] Could not refresh bookmark status:',
                        err,
                      )
                    }
                  }
                }
              : undefined
          }
          tabBookmarkStatus={tabBookmarkStatus ?? undefined}
        />
      )}
    </>
  )
}
